/**
 * @file ProfilePage.jsx — the signed-in user's own details, READ-ONLY.
 *
 * Shows exactly four things — name, email, phone, department — and nothing
 * else. There is no edit path on purpose: user records are maintained by the
 * admin in Organization, so nobody can change their own data here. Values
 * come from GET /auth/profile (the users row), never from a placeholder.
 */
import React, { useEffect, useState } from 'react';
import { Box, Container, Paper, Typography, Avatar, Divider, Skeleton, Alert } from '@mui/material';
import {
    Person as PersonIcon, Email as EmailIcon, Phone as PhoneIcon, Apartment as DepartmentIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import client from '../../api/client';

const initials = (name) => (name ? name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : 'U');

const Field = ({ Icon, label, value }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
        <Box sx={{ width: 40, height: 40, borderRadius: '12px', bgcolor: 'primary.light', color: 'primary.dark',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, opacity: 0.9 }}>
            <Icon fontSize="small" />
        </Box>
        <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {label}
            </Typography>
            <Typography variant="body1" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
                {value || <Box component="span" sx={{ color: 'text.disabled', fontWeight: 400 }}>Not set</Box>}
            </Typography>
        </Box>
    </Box>
);

const ProfilePage = () => {
    const { user: sessionUser } = useAuth();
    const [me, setMe] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const res = await client.get('/auth/profile');
                if (alive && res?.success) setMe(res.data);
            } catch (e) {
                if (alive) setError('Could not load your details right now.');
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, []);

    // The session user is a fallback while the row loads (it has no phone).
    const u = me || sessionUser || {};

    return (
        <Container maxWidth="sm" sx={{ py: 4 }}>
            <Box sx={{ mb: 3, textAlign: 'center' }}>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>Personal info</Typography>
                <Typography variant="body2" color="text.secondary">
                    Your details as held by MTANDT. To change them, ask your administrator.
                </Typography>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>{error}</Alert>}

            <Paper elevation={0} sx={{ p: { xs: 3, md: 4 }, borderRadius: '16px', border: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 2 }}>
                    <Avatar sx={{ width: 72, height: 72, fontSize: '1.75rem', fontWeight: 700,
                        background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' }}>
                        {initials(u.name)}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                        {loading && !u.name ? <Skeleton width={180} height={32} /> : (
                            <Typography variant="h5" sx={{ fontWeight: 700 }} noWrap>{u.name || '—'}</Typography>
                        )}
                        <Typography variant="body2" color="text.secondary">
                            {[u.role, u.company].filter(Boolean).join(' • ') || ' '}
                        </Typography>
                    </Box>
                </Box>

                <Divider />

                {loading && !me ? (
                    <Box sx={{ pt: 1 }}>{[...Array(4)].map((_, i) => <Skeleton key={i} height={56} />)}</Box>
                ) : (
                    <Box sx={{ '& > div + div': { borderTop: '1px solid', borderColor: 'divider' } }}>
                        <Field Icon={PersonIcon} label="Name" value={u.name} />
                        <Field Icon={EmailIcon} label="Email" value={u.email} />
                        <Field Icon={PhoneIcon} label="Phone number" value={u.mobile_number} />
                        <Field Icon={DepartmentIcon} label="Department" value={u.department} />
                    </Box>
                )}
            </Paper>
        </Container>
    );
};

export default ProfilePage;
